/**
 * apiFetch — thin wrapper around fetch() that automatically injects:
 *   - x-org-subdomain header (for tenant resolution on public routes)
 *   - Authorization: Bearer <token> (when a JWT is stored in localStorage)
 *
 * On 401 responses it clears the stored token and dispatches a custom
 * 'shufflr:auth-expired' event so the app can redirect to /login.
 */

const TOKEN_KEY = 'shufflr_token';

export interface ApiFetchOptions extends RequestInit {
  /** Org slug — injected as x-org-subdomain. */
  slug?: string | null;
  /** Override token (defaults to localStorage value). */
  token?: string | null;
}

export async function apiFetch(
  path: string,
  { slug, token, ...init }: ApiFetchOptions = {},
): Promise<Response> {
  const storedToken = token ?? localStorage.getItem(TOKEN_KEY);

  const headers = new Headers(init.headers ?? {});

  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  if (slug) {
    headers.set('x-org-subdomain', slug);
  }

  if (storedToken) {
    headers.set('Authorization', `Bearer ${storedToken}`);
  }

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401) {
    // Token expired or invalid — clear it and notify the app.
    localStorage.removeItem(TOKEN_KEY);
    window.dispatchEvent(new CustomEvent('shufflr:auth-expired'));
  }

  return res;
}
